export type CompletedMultipartPart = {
  PartNumber: number;
  ETag: string;
};

export const normalizeCompletedParts = (parts: unknown): CompletedMultipartPart[] => {
  if (!Array.isArray(parts)) return [];

  const partsByNumber = new Map<number, CompletedMultipartPart>();
  parts.forEach((part) => {
    const partNumber = Number(part?.PartNumber);
    const eTag = typeof part?.ETag === 'string' ? part.ETag : '';
    if (Number.isInteger(partNumber) && partNumber > 0 && eTag) {
      partsByNumber.set(partNumber, { PartNumber: partNumber, ETag: eTag });
    }
  });

  return Array.from(partsByNumber.values()).sort((left, right) => left.PartNumber - right.PartNumber);
};

export const getLocalCompletedPartsForUpload = (
  uploadId: string,
  storedUploadId: string,
  storedParts: unknown,
): CompletedMultipartPart[] => {
  return uploadId && uploadId === storedUploadId ? normalizeCompletedParts(storedParts) : [];
};

export const getMissingPartNumbers = (totalParts: number, completedParts: unknown): number[] => {
  const normalizedTotal = Math.max(0, Math.floor(totalParts));
  const completedPartNumbers = new Set(normalizeCompletedParts(completedParts).map((part) => part.PartNumber));

  return Array.from({ length: normalizedTotal }, (_, index) => index + 1).filter(
    (partNumber) => !completedPartNumbers.has(partNumber),
  );
};

export const hasEveryPart = (totalParts: number, completedParts: unknown): boolean => {
  const normalizedParts = normalizeCompletedParts(completedParts).filter((part) => part.PartNumber <= totalParts);
  return normalizedParts.length === totalParts && getMissingPartNumbers(totalParts, normalizedParts).length === 0;
};

export const getUploadedBytesForParts = (fileSize: number, partSize: number, completedParts: unknown): number => {
  if (fileSize <= 0 || partSize <= 0) return 0;

  const totalParts = Math.ceil(fileSize / partSize);
  return normalizeCompletedParts(completedParts)
    .filter((part) => part.PartNumber <= totalParts)
    .reduce((total, part) => {
      const start = (part.PartNumber - 1) * partSize;
      return total + Math.max(0, Math.min(partSize, fileSize - start));
    }, 0);
};

export const getUploadedBytesForStoredUpload = (
  totalSize: number,
  partSize: number,
  videos: unknown,
  metadataUploaded: boolean,
): number => {
  if (!Array.isArray(videos)) return 0;

  const normalizedTotalSize = Math.max(0, Number(totalSize) || 0);
  const videosSize = videos.reduce((total, video) => total + Math.max(0, Number(video?.size) || 0), 0);
  const uploadedVideoBytes = videos.reduce((total, video) => {
    const videoSize = Math.max(0, Number(video?.size) || 0);
    return total + (video?.status === 'uploaded' ? videoSize : getUploadedBytesForParts(videoSize, partSize, video?.chunks));
  }, 0);
  const uploadedMetadataBytes = metadataUploaded ? Math.max(0, normalizedTotalSize - videosSize) : 0;
  const uploadedBytes = uploadedVideoBytes + uploadedMetadataBytes;

  return normalizedTotalSize ? Math.min(normalizedTotalSize, uploadedBytes) : uploadedBytes;
};

export const runWithConcurrency = async <T>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T) => Promise<void>,
  onError?: (error: unknown) => void,
): Promise<void> => {
  if (!items.length) return;

  const workerCount = Math.min(items.length, Math.max(1, Math.floor(concurrency)));
  let nextIndex = 0;
  let hasError = false;
  let firstError: unknown;

  const runWorker = async () => {
    while (!hasError) {
      const itemIndex = nextIndex++;
      if (itemIndex >= items.length) return;

      try {
        await worker(items[itemIndex]);
      } catch (error) {
        if (!hasError) {
          hasError = true;
          firstError = error;
          onError?.(error);
        }
      }
    }
  };

  await Promise.all(Array.from({ length: workerCount }, runWorker));
  if (hasError) throw firstError;
};
